/**
 * The Results hero (DESIGN §3a): the verdict in dollars a share against the twin, the tier, rank and limit chips, and the score as four cells that add to the cent.
 * One paragraph of words at most; everything else is a figure. The Details fold of the old headline is gone: the score's parts are the cells, the reason is one line.
 */
import type { ReactNode } from "react";
import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import { formatMetric, TWIN_DEFINITION } from "@gxs/metric-defs";
import { formatPointsWord, formatUsdAShare, formatUsdPerShare } from "../format";
import { GxDescribed } from "../theme/GxTerm";
import { GxPill } from "../theme/GxPill";
import { printedParts } from "../score/labels";
import { SPLIT_LEG } from "../score/splitWords";
import { TIER_PROVISIONAL, TIER_PROVISIONAL_SHORT, TIER_PROVISIONAL_WHY, TierChip } from "./TierChip";
import { yearsAheadSentence } from "./yearsAhead";
import { yearSurpriseLine } from "./learningSurprise";
import { limitsBreached } from "./Breaches";
import { previewVersusYearSentence } from "./yearStory";
import { landedNearPreview, offsettingSentence } from "./yourCalls";
import { biggestLeg, mainReasonClause } from "./mainReason";
import "./results.css";

/** Under half a cent a figure reads as no move. */
const LEVEL_USD = 0.005;

/** "2.9 percentage points of its value per share": the one way the page writes the official score's unit. */
export const percentagePointsOfValue = (rate: number): string => `${formatMetric(Math.abs(rate) * 100, "index", { precision: 2 })} percentage points of its value per share`;

/** The verdict as the heading says it: which side of the twin, and the figure that gets the colour. */
export function verdictParts(usdPerShare: number): { readonly lead: string; readonly figure: string; readonly tail: string; readonly side: "ahead" | "behind" | "level" } {
  if (Math.abs(usdPerShare) < LEVEL_USD) return { lead: "Level with your twin: ", figure: formatUsdAShare(0), tail: ".", side: "level" };
  return usdPerShare > 0
    ? { lead: "You created ", figure: formatUsdAShare(usdPerShare), tail: " more than your twin.", side: "ahead" }
    : { lead: "You created ", figure: formatUsdAShare(-usdPerShare), tail: " less than your twin.", side: "behind" };
}

export interface HeroRank {
  /** "1st of 6 banks, up from 2nd"; null while the final rank is held back. */
  readonly text: string | null;
  /** The note on a tie or a close finish, kept as the chip's tooltip. */
  readonly note?: string | undefined;
  /** Before the final round: its number. The tier is awarded each year but is provisional until the finale (OR-30.11); the cockpit and Results both read it from here. */
  readonly provisionalUntil?: number | undefined;
}

const Cell = ({ value, label, total = false }: { readonly value: string; readonly label: string; readonly total?: boolean }) => (
  <div className={`gx-rhero-cell${total ? " gx-rhero-cell--total" : ""}`}>
    <b className="gx-num" {...(total ? { "data-testid": "score.split.sum" } : {})}>{value}</b>
    <span>{label}</span>
  </div>
);
/** A minus sign never ends a line: the word joiner glues it to its figure. */
export const gluedMinus = (text: string): string => text.replace(/\u2212(?=\s?\$?\d)/g, "\u2212\u2060");
const Op = ({ children }: { readonly children: string }) => <span className="gx-rhero-op" aria-hidden="true">{children}</span>;

export function ResultsHero({ report, catalog, rank, firstRound, onMoved, limits, reveal = false, compact = false, draft = null, provisional }: {
  /** Before the last round: the final round's number; the look-through cell and the tier then say they can still change until that year (DESIGN-RESULTS §8). */
  readonly provisional?: number | undefined;
  readonly draft?: DraftView | null;
  readonly report: RoundReport; readonly catalog: ContentCatalog; readonly rank: HeroRank;
  /** The first year: nothing was changed, so a level result is explained by the standing policy, not by the market. */
  readonly firstRound: boolean;
  /** "See what moved it" scrolls to that section. */
  readonly onMoved: () => void;
  /** Where the limits card is, for the breached chip. */
  readonly limits?: { readonly breached: number; readonly onSee: () => void } | undefined;
  readonly reveal?: boolean;
  /** The cockpit's shorter form: the heading, the chips and the four cells, no reason line. */
  readonly compact?: boolean;
}) {
  const c = report.score.components;
  const usd = report.score.valueCreatedUsdPerShare;
  const verdict = verdictParts(usd);
  const parts = c === undefined ? null : printedParts(c, usd);
  const allZero = parts !== null && [parts.price, parts.cash, parts.lookThrough, usd].every((v) => Math.abs(v) < LEVEL_USD);
  const leg = biggestLeg(report, catalog, draft);
  const money = (v: number): string => formatUsdPerShare(v, { signed: true });
  const tierProvisional = provisional !== undefined || rank.provisionalUntil !== undefined;
  const surprise = yearSurpriseLine(report);
  const ahead = compact ? null : yearsAheadSentence(report, catalog, draft);
  const breached = limits?.breached ?? limitsBreached([report]);
  // When large moves cancelled, "the year came in as the preview expected" would hide the news; the calls block under the hero says what cancelled.
  const preview = offsettingSentence(report) !== null && landedNearPreview(report) ? null : previewVersusYearSentence(report);
  const reason: ReactNode = verdict.side === "level" && firstRound
    ? "You kept your standing policy, so this year looks the same as your twin's."
    : leg === null ? null : <>{preview === null ? null : <span data-testid="results.hero.preview">{gluedMinus(preview)} </span>}{preview !== null && leg.kind === "preview" ? null : <>{mainReasonClause(leg)}. </>}<button type="button" className="gx-rhero-link" data-testid="results.hero.moved" onClick={onMoved}>See what moved it</button></>;
  // OR-30.40: the hero is one sentence and one chip row; what surprised and what the years ahead hold sit in one short block directly under it.
  // OR-33.2: the played years beside the official score, every round; the look-through keeps its full weight.
  const rate = report.scorecard.reportedOnlyValueCreatedRate;
  const asPlayedUsd = parts === null ? null : Math.round((parts.price + parts.cash) * 100) / 100;
  const revalUsd = parts === null ? null : parts.lookThrough;
  const revalVerb = revalUsd === null ? null : Math.abs(revalUsd) < LEVEL_USD ? "changed nothing" : revalUsd < 0 ? "took back" : "added";
  const bothLines = compact || asPlayedUsd === null || revalUsd === null || allZero ? null : (
    <div className="gx-rhero-bothlines" data-testid="results.hero.bothlines">
      <p className="gx-rhero-line" data-testid="results.hero.asplayed">As played, on reported numbers alone: <b className="gx-num">{money(asPlayedUsd)} a share · {formatPointsWord(rate, { signed: true, dp: 2 })}</b></p>
      <p className="gx-rhero-line" data-testid="results.hero.official">Official, with the {SPLIT_LEG.lookThrough.label.toLowerCase()}: <b className="gx-num">{money(usd)} a share · {formatPointsWord(report.score.valueCreatedRate, { signed: true, dp: 2 })}</b></p>
      <p className="gx-rhero-note" data-testid="results.hero.revaluation">{revalVerb === "changed nothing" ? "The look-through revaluation changed nothing: the next two years are expected to earn what the year-end price already reflects." : <>The look-through revaluation {revalVerb} <b className="gx-num">{formatUsdPerShare(Math.abs(revalUsd))} a share</b> because it values the bank on what the next two years are expected to earn, not on the year-end price.</>}</p>
    </div>
  );
  const moved = compact || (surprise === null && reason === null) ? null : (
    <div className="gx-rhero-moved" data-testid="results.hero.moved">
      {surprise === null ? null : <p className="gx-rhero-note" data-testid="results.hero.surprise">{gluedMinus(surprise)}</p>}
      {reason === null ? null : <p className="gx-rhero-why" data-testid="score.reason">{ahead === null ? null : <span data-testid="results.hero.yearsahead">{gluedMinus(ahead)} </span>}{reason}</p>}
    </div>
  );
  return (
    <>
    <section className={`gx-hero gx-on-navy gx-rhero${reveal ? " gx-results-reveal" : ""}${compact ? " gx-rhero--compact" : ""}`} data-testid="results.hero" aria-label="Your result against your twin">
      <div className="gx-rhero-main" data-testid="score">
        <h2 className="gx-rhero-h" data-testid="score.standing" data-side={verdict.side}>{verdict.lead}<span className="gx-rhero-fig">{verdict.figure}</span>{verdict.tail}</h2>
        <p className="gx-rhero-sub" data-testid="results.hero.sub">Your twin is {TWIN_DEFINITION[0]?.toLowerCase()}{TWIN_DEFINITION.slice(1)}</p>
        <div className="gx-rhero-chips">
          <span data-testid="results.hero.tier"><TierChip report={report} catalog={catalog} provisional={tierProvisional} gap={verdict.side === "level" ? null : percentagePointsOfValue(report.score.valueCreatedRate)} /></span>
          {!tierProvisional ? null : <GxDescribed label={TIER_PROVISIONAL_SHORT} plain={`${TIER_PROVISIONAL}. ${TIER_PROVISIONAL_WHY}`}><GxPill tone="neutral" testId="results.hero.tierprov">{TIER_PROVISIONAL_SHORT}</GxPill></GxDescribed>}
          {rank.text === null ? <GxPill variant="count" testId="results.rankmove">Final rank revealed at the finale</GxPill> : rank.note === undefined ? <GxPill variant="count" testId="results.rankmove">{rank.text}</GxPill> : <GxDescribed label={rank.text} plain={rank.note}><GxPill variant="count" testId="results.rankmove">{rank.text}</GxPill></GxDescribed>}
          {breached === 0
            ? <span data-testid="results.hero.limits"><GxPill tone="good">No limit breached</GxPill></span>
            : <button type="button" className="gx-pill-btn" data-testid="results.hero.limits" onClick={limits?.onSee}><GxPill tone="crit">{breached} {breached === 1 ? "limit" : "limits"} breached</GxPill></button>}
        </div>
      </div>
      <div className="gx-rhero-eq">
        {allZero ? <p className="gx-rhero-why" data-testid="results.hero.nothing">Nothing to add up: your bank and its twin ran the same plan, so every part of the score is zero.</p> : <p className="gx-rhero-cap">Where the {formatUsdPerShare(usd)} comes from, per share held at the start, in today&apos;s dollars</p>}
        {parts === null || allZero ? null : (
          <div className="gx-rhero-cells gx-num" data-testid="score.split">
            <Cell value={money(parts.price)} label="Share price level, against your twin's" />
            <Cell value={money(parts.cash)} label="Cash returned since the start, against your twin's" />
            <Cell value={money(parts.lookThrough)} label={`${SPLIT_LEG.lookThrough.label}: the official score less price and cash`} />
            <Op>=</Op>
            <Cell value={money(usd)} label="Value created, your score" total />
          </div>
        )}
      </div>
    </section>
    {bothLines}
    {moved}
    </>
  );
}

export { biggestLeg };
